import type { ReactNode } from 'react'
import { LumpsumAllocationCategoryExecutionStatus } from '../../../api/allocation.types'
import {
  LumpsumAllocationBuyReasonType,
  type LumpsumAllocationBuyReason
} from '../../api/allocationRecommendation.types'
import {
  normalizePlanPercentage,
  planPercentageLabel
} from '../../../../display-values/lumpsumPlanPercentage'
import {
  ReasonPointer,
  ReasonPointerHighlightStrip,
  ReasonPointerRankHighlightStrip
} from './ReasonPointerUi'

enum BuyReasonExecutionScope {
  ALLOCATION = 'ALLOCATION',
  FUND_COUNT = 'FUND_COUNT'
}

function pickCategoryExecutionStatus(
  executionScope: BuyReasonExecutionScope,
  categoryAllocationExecutionStatus?: LumpsumAllocationCategoryExecutionStatus | string | null,
  categoryFundCountExecutionStatus?: LumpsumAllocationCategoryExecutionStatus | string | null
): LumpsumAllocationCategoryExecutionStatus | string | null {
  if (executionScope === BuyReasonExecutionScope.ALLOCATION) {
    return categoryAllocationExecutionStatus ?? null
  }
  return categoryFundCountExecutionStatus ?? null
}

type BuyReasonPointerData = {
  reason: LumpsumAllocationBuyReason
  index: number
  buyCategoryName: string
  buyPowerRank: number | null
  buyPowerRating: string | null
  isBuyExecuted: boolean
  categoryAllocationExecutionStatus?: LumpsumAllocationCategoryExecutionStatus | string | null
  categoryFundCountExecutionStatus?: LumpsumAllocationCategoryExecutionStatus | string | null
}

type BuyReasonPointerWithExecutionScope = BuyReasonPointerData & {
  executionScope: BuyReasonExecutionScope
}

/** Allocation buy reason pointers always use positive tone (including rank). */

export function getBuyReasonSubtitleLabel(reason: LumpsumAllocationBuyReason): string {
  switch (reason?.reason) {
    case LumpsumAllocationBuyReasonType.TOP_RANKED_FUND:
      return 'Excellent Power Rank'
    case LumpsumAllocationBuyReasonType.INCREASES_EXPOSURE:
      return 'Increases exposure'
    case LumpsumAllocationBuyReasonType.ADDS_EXPOSURE:
      return 'Adds exposure'
    case LumpsumAllocationBuyReasonType.IMPROVES_DIVERSIFICATION:
      return 'Improves diversification'
    case LumpsumAllocationBuyReasonType.CONSOLIDATE_FUNDS:
      return 'Consolidate Funds'
    case LumpsumAllocationBuyReasonType.IMPROVES_PERFORMANCE:
      return 'Improves performance'
    default:
      return ''
  }
}

export function BuyReasonPointer(data: BuyReasonPointerData): ReactNode {
  switch (data.reason?.reason) {
    case LumpsumAllocationBuyReasonType.TOP_RANKED_FUND: {
      return <TopRankedFundBuyReasonPointer {...data} />
    }
    case LumpsumAllocationBuyReasonType.INCREASES_EXPOSURE: {
      return <IncreasesExposureBuyReasonPointer {...data} />
    }
    case LumpsumAllocationBuyReasonType.ADDS_EXPOSURE: {
      return <AddsExposureBuyReasonPointer {...data} />
    }
    case LumpsumAllocationBuyReasonType.IMPROVES_DIVERSIFICATION: {
      const executionScope = BuyReasonExecutionScope.FUND_COUNT
      return <ImprovesDiversificationBuyReasonPointer {...data} executionScope={executionScope} />
    }
    case LumpsumAllocationBuyReasonType.CONSOLIDATE_FUNDS: {
      return <ConsolidateFundsBuyReasonPointer {...data} />
    }
    case LumpsumAllocationBuyReasonType.IMPROVES_PERFORMANCE: {
      return <ImprovesPerformanceBuyReasonPointer {...data} />
    }
    default: {
      return null
    }
  }
}

export function TopRankedFundBuyReasonPointer({
  index,
  buyPowerRank,
  buyPowerRating
}: BuyReasonPointerData) {
  return (
    <ReasonPointer
      index={index}
      title="Excellent Power Rank"
      subtitle="in its category"
      highlightContent={(
        <ReasonPointerRankHighlightStrip
          powerRank={buyPowerRank}
          powerRating={buyPowerRating}
          description="Top ranked, outperforming the benchmark and your current fund"
          tone="positive"
        />
      )}
    />
  )
}

export function IncreasesExposureBuyReasonPointer(data: BuyReasonPointerData) {
  return (
    <ExposureChangeBuyReasonPointer
      {...data}
      title="Increases exposure"
      highlightVerb="Increasing"
    />
  )
}

export function AddsExposureBuyReasonPointer(data: BuyReasonPointerData) {
  return (
    <ExposureChangeBuyReasonPointer
      {...data}
      title="Adds exposure"
      highlightVerb="Adding"
    />
  )
}

export function ExposureChangeBuyReasonPointer({
  reason,
  index,
  buyCategoryName,
  isBuyExecuted,
  title,
  highlightVerb
}: BuyReasonPointerData & {
  title: string
  highlightVerb: 'Increasing' | 'Adding'
}) {
  const categoryName = buyCategoryName || 'this category'
  const currentCategoryAllocationPercentage = reason?.currentCategoryAllocationPercentage ?? 0
  const idealCategoryAllocationPercentage = reason?.idealCategoryAllocationPercentage ?? 0

  let highlightTitle: string | undefined
  if (isBuyExecuted) {
    highlightTitle = undefined
  } else {
    highlightTitle = `+${planPercentageLabel({
      rawPercentage: normalizePlanPercentage(
        Math.max(idealCategoryAllocationPercentage - currentCategoryAllocationPercentage, 0)
      )
    })}`
  }

  return (
    <ReasonPointer
      index={index}
      title={title}
      subtitle={`in ${categoryName}`}
      highlightTitle={highlightTitle}
      highlightDescription={`${highlightVerb} exposure in ${categoryName} helps you reach ideal allocation of ${planPercentageLabel({ rawPercentage: idealCategoryAllocationPercentage })} as per your strategy.`}
      tone="positive"
    />
  )
}

export function ImprovesDiversificationBuyReasonPointer({
  reason,
  index,
  buyCategoryName,
  isBuyExecuted,
  executionScope,
  categoryAllocationExecutionStatus,
  categoryFundCountExecutionStatus
}: BuyReasonPointerWithExecutionScope) {
  const categoryExecutionStatus = pickCategoryExecutionStatus(
    executionScope,
    categoryAllocationExecutionStatus,
    categoryFundCountExecutionStatus
  )
  const categoryName = buyCategoryName || 'this category'
  const idealCategoryFundCount = reason?.idealCategoryFundCount ?? 0
  const progressiveDescription = `Adding this fund helps you diversify to ${idealCategoryFundCount} funds in ${categoryName}.`
  const notStartedDescription = `Adding this fund helps you diversify from ${reason?.currentCategoryFundCount ?? 0} to ${idealCategoryFundCount} funds in ${categoryName}.`

  let highlightDescription: string
  if (isBuyExecuted) {
    highlightDescription = progressiveDescription
  } else {
    if (categoryExecutionStatus === LumpsumAllocationCategoryExecutionStatus.IN_PROGRESS) {
      highlightDescription = progressiveDescription
    } else {
      highlightDescription = notStartedDescription
    }
  }

  return (
    <ReasonPointer
      index={index}
      title="Improves diversification"
      subtitle={`in ${categoryName}`}
      highlightContent={(
        <ReasonPointerHighlightStrip
          highlightTitle=""
          highlightDescription={highlightDescription}
          tone="positive"
        />
      )}
    />
  )
}

export function ConsolidateFundsBuyReasonPointer({
  reason,
  index,
  buyCategoryName,
  isBuyExecuted
}: BuyReasonPointerData) {
  const categoryName = buyCategoryName || 'this category'
  const idealCategoryFundCount = reason?.idealCategoryFundCount ?? 0
  const progressiveDescription = `This recommendation helps in consolidating to ${idealCategoryFundCount} funds in ${categoryName}.`
  const notStartedDescription = `This recommendation helps in consolidating from ${reason?.currentCategoryFundCount ?? 0} to ${idealCategoryFundCount} funds in ${categoryName}.`

  let highlightDescription: string
  if (isBuyExecuted) {
    highlightDescription = progressiveDescription
  } else {
    highlightDescription = notStartedDescription
  }

  return (
    <ReasonPointer
      index={index}
      title="Consolidate Funds"
      subtitle={`in ${categoryName}`}
      highlightContent={(
        <ReasonPointerHighlightStrip
          highlightTitle=""
          highlightDescription={highlightDescription}
          tone="positive"
        />
      )}
    />
  )
}

export function ImprovesPerformanceBuyReasonPointer({
  reason,
  index,
  buyCategoryName
}: BuyReasonPointerData) {
  const categoryName = buyCategoryName || 'this category'

  return (
    <ReasonPointer
      index={index}
      title="Improves performance"
      subtitle={`in ${categoryName}`}
      highlightTitle={`+ ${planPercentageLabel({ rawPercentage: reason?.xirrDiff ?? 0 })} XIRR`}
      highlightDescription={`Higher returns in the last ${reason?.xirrDiffInMonths ?? 0} months, and is expected to maintain this performance`}
      tone="positive"
    />
  )
}
